import { PERMISSIONS } from '@/shared/constants/permissions';
import z from 'zod';

export const PermissionSchema = z.enum(PERMISSIONS);

export const EmrIntegrationDtoSchema = z.object({
  authType: z.enum(['BASIC']),
  provider: z.enum(['ALIS']),
  createdAt: z.string(),
  lastSyncedAt: z.string().nullable(),
  updatedAt: z.string(),
});

export const UserRoleDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  organizationId: z.string().nullable(),
});

export const OrganizationOwnerDtoSchema = z.object({
  createdAt: z.string(),
  email: z.email(),
  fullName: z.string(),
  id: z.number(),
  lastLogin: z.string().nullable(),
  updatedAt: z.string(),
});

export const OrganizationDtoSchema = z.object({
  id: z.number(),
  address: z.string(),
  createdAt: z.string(),
  name: z.string(),
  ownerId: z.number().nullable(),
  siteUrl: z.string(),
  updatedAt: z.string(),
  emrIntegration: EmrIntegrationDtoSchema.nullable(),
  owner: OrganizationOwnerDtoSchema.nullable(),
  isDemo: z.boolean().optional(),
  hideCompSetToggle: z.boolean().optional(),
  staffSource: z.enum(['ALIS', 'SLING']).optional(),
});

export const UserDtoSchema = z.object({
  id: z.number(),
  email: z.email(),
  fullName: z.string(),
  organizationId: z.number().nullable(),
  managedFacilities: z.number().nullable(),
  createdBy: z
    .object({
      id: z.number(),
      email: z.email(),
      fullName: z.string(),
    })
    .nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastLogin: z.string().nullable(),
  invitedAt: z.string().nullable().optional(),
  role: UserRoleDtoSchema.nullable(),
  profileUrl: z.string().nullable(),
  organization: OrganizationDtoSchema.nullable(),
  permissions: z.array(PermissionSchema).nullable(),
  deletedAt: z.string().nullable(),
  isDemoUser: z.boolean().optional(),
  canImpersonate: z.boolean().optional(),
  canDownloadStaffTemplate: z.boolean().optional(),
  earliestSnapshotDate: z.string().nullable().optional(),
});

export const FacilityDtoSchema = z.object({
  id: z.number(),
  name: z.string(),
  yearBuilt: z.number().nullable().optional(),
  occupancy: z.object({
    changePct: z.number().nullable(),
    currentAvg: z.number().nullable(),
  }),
  managers: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        email: z.email(),
      })
    )
    .optional(),
  pricingModel: z
    .object({ id: z.number(), name: z.string() })
    .nullable()
    .optional(),
  types: z
    .array(
      z.enum([
        'ASSISTED_LIVING',
        'MEMORY_CARE',
        'INDEPENDENT_LIVING',
        'SKILLED_NURSING',
      ])
    )
    .optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']),
  bedsAvg: z.number().nullable(),
  bedsHighest: z.number().nullable().optional(),
  staffAvg: z.number().nullable(),
  residents: z.object({
    currentAvg: z.number().nullable(),
    changePct: z.number().nullable(),
    residentsTotal: z.number().nullable(),
  }),
  staffingRatio: z.object({
    currentAvg: z.number().nullable(),
    changePct: z.number().nullable(),
    // HPRD for staff from Sling; the old ratio otherwise.
    metric: z.enum(['RATIO', 'HPRD']).optional(),
  }),
  payerPrivatePct: z.number().nullable().optional(),
  payerMedicaidPct: z.number().nullable().optional(),
  hasPayerTypes: z.boolean(),
  createdFlags: z.number().optional(),
  createdAlerts: z.number().optional(),
  /* Financial tab columns (ST #137). Optional: the Operational table asks for
     none of them, and zod strips anything not declared here. */
  expenses: z.number().nullable().optional(),
  expensesInsufficientData: z.boolean().optional(),
  marginAvgPct: z.number().nullable().optional(),
  revenuePerResidentAvg: z.number().nullable().optional(),
  revenuePerBedAvg: z.number().nullable().optional(),
  payerPrivateRevenuePct: z.number().nullable().optional(),
  payerMedicaidRevenuePct: z.number().nullable().optional(),
  revenue: z
    .object({
      amountUsd: z.number().nullable(),
      changePct: z.number().nullable(),
    })
    .optional(),
  monthlyRevenue: z
    .object({
      amountUsd: z.number().nullable(),
      changePct: z.number().nullable(),
    })
    .optional(),
  revenuePerResident: z
    .object({
      amountUsd: z.number().nullable(),
      changePct: z.number().nullable(),
    })
    .optional(),
  coordinates: z.object({
    lat: z.number().nullable(),
    lng: z.number().nullable(),
  }),
  address: z.string().nullable().optional(),
});

export const FacilitySinglePageDtoSchema = z.object({
  id: z.number(),
  name: z.string(),
  coordinates: z.object({
    lat: z.number().nullable(),
    lng: z.number().nullable(),
  }),
  address: z.string().nullable(),
  status: z.enum(['ACTIVE', 'INACTIVE']),
  phoneNumber: z.string().nullable(),
  pricingModel: z.object({ id: z.number(), name: z.string() }).nullable(),
  totalBeds: z.number().nullable(),
  totalStaff: z.number().nullable(),
  activeResidentsCount: z.number(),
  types: z
    .array(
      z.enum([
        'ASSISTED_LIVING',
        'MEMORY_CARE',
        'INDEPENDENT_LIVING',
        'SKILLED_NURSING',
      ])
    )
    .nullable(),
  yearBuilt: z.number().nullable(),
  managers: z.array(
    z.object({ id: z.number(), name: z.string(), email: z.email() })
  ),
});

export const AmenityDtoSchema = z.object({
  id: z.number(),
  name: z.string(),
  isSelected: z.boolean(),
});

export const StaffNotesDtoSchema = z.object({
  id: z.number(),
  noteText: z.string(),
  takenBy: z.string(),
  creationDate: z.coerce.date(),
});

export const FamilyCommunicationsDtoSchema = z.object({
  date: z.coerce.date(),
  fileUrl: z.string(),
  originalFileName: z.string().nullable(),
  id: z.number(),
  residentId: z.number(),
  title: z.string(),
  uploadedBy: z.object({
    id: z.number(),
    fullName: z.string(),
  }),
});

export const ResidentNotesDtoSchema = z.object({
  id: z.number(),
  noteText: z.string(),
  takenBy: z.string(),
  creationDate: z.coerce.date(),
  takenById: z.number(),
});

export const StaffWageHistory = z.object({
  lastReviewAt: z.coerce.date(),
  madeBy: z.string().nullable(),
  reviewResult: z.string().nullable(),
});

export const StaffDtoSchema = z.object({
  id: z.number(),
  facilityName: z.string(),
  payType: z.enum(['HOURLY', 'SALARY']).nullable(),
  source: z.enum(['ALIS', 'SLING']),
  annualSalary: z.number().nullable(),
  daysSinceLastReview: z.number().nullable(),
  dateOfHire: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  fullName: z.string(),
  loginTime: z.string().nullable(),
  lastLogAt: z.string().nullable().optional(),
  jobRole: z.string(),
  wageRate: z.number().nullable(),
  wageType: z.enum(['HOURLY', 'SALARY']).nullable(),
});

export const SparkLinePointDtoSchema = z.object({
  end: z.string(),
  start: z.string(),
  value: z.number().nullable(),
});

export const MarketFacilityListItemDtoSchema = z.object({
  id: z.number(),
  name: z.string(),
  dba: z.string(),
  address: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  website: z.string().nullable(),
});

export const MarketFacilityVersionFormDtoSchema = z.object({
  id: z.number(),
  contractorName: z.string().nullable(),
  submittedAt: z.string().nullable(),
  version: z.number(),
  status: z.enum(['DRAFT', 'PUBLISHED']),
});

export const AlertConfigDtoSchema = z.object({
  id: z.number(),
  originalName: z.string(),
  displayName: z.string(),
  enableDropdown: z.boolean(),
  dropdownName: z.string().nullable(),
  options: z.array(
    z.object({
      id: z.number(),
      label: z.string(),
      priority: z.enum(['LOW', 'MEDIUM', 'HIGH']),
      timeToResolveDays: z.number(),
      points: z.number(),
      pointsResetDays: z.number(),
      allowMultipleResolutions: z.boolean(),
      repeatEnabled: z.boolean(),
      repeatFrequencyDays: z.number().nullable(),
      repeatAlertPoints: z.number().nullable(),
      sortOrder: z.number(),
      resolutions: z.array(
        z.object({
          id: z.number(),
          label: z.string(),
          sortOrder: z.number(),
        })
      ),
    })
  ),
});

const MarketIntelRowSchema = z.object({
  owned: z.number().nullable(),
  comp: z.number().nullable(),
  delta: z.number().nullable(),
});

export const MarketIntelDtoSchema = z.object({
  occupancy: MarketIntelRowSchema,
  averageMonthlyRate: MarketIntelRowSchema,
  residentTurnover: MarketIntelRowSchema,
  margin: MarketIntelRowSchema,
  totalAdmissions: MarketIntelRowSchema,
  totalDischarges: MarketIntelRowSchema,
  title: z.string(),
  subtitle: z.string(),
});

export const AiSummaryDtoSchema = z.object({
  summary: z.string(),
  recommendation: z.string(),
});

export const CompetitorsLocationsDtoSchema = z.object({
  name: z.string(),
  coordinates: z.object({ lat: z.number(), lng: z.number() }),
});

// Market Facilities Form schemas
export const FacilityTypeEnum = z.enum([
  'ASSISTED_LIVING',
  'MEMORY_CARE',
  'INDEPENDENT_LIVING',
  'SKILLED_NURSING',
]);

export const CareStructureEnum = z.enum([
  'ALL_INCLUSIVE',
  'A_LA_CARTE',
  'POINTS',
  'TIERED',
  'ENTRANCE_PLUS_RENT',
]);

export const MarketPositionEnum = z.enum(['BUDGET', 'MID', 'PREMIUM']);

export const FormStatusEnum = z.enum(['DRAFT', 'PUBLISHED']);

export const RoomTypeEnum = z.enum([
  'PRIVATE_ROOM',
  'SEMI_PRIVATE_ROOM',
  'SHARED_ROOM',
  'STUDIO',
  'ONE_BEDROOM_APT',
  'TWO_BEDROOM_APT',
  'SUITE',
  'MEMORY_CARE_UNIT',
]);

export const AmenityPriorityEnum = z.enum([
  'CRITICAL',
  'HIGH',
  'MEDIUM',
  'LOW',
]);

export const MarketFacilityCreateCareLevelDtoSchema = z.object({
  cost: z.number(),
  name: z.string(),
  sortOrder: z.number().optional(),
});

export const MarketFacilityCreateRoomTypeDtoSchema = z.object({
  baseRate: z.number(),
  type: RoomTypeEnum,
  sortOrder: z.number().optional(),
});

export const MarketFacilityCreateExtraServiceDtoSchema = z.object({
  name: z.string(),
  price: z.number(),
  sortOrder: z.number().optional(),
});

export const MarketFacilityFormResponseCareLevelDtoSchema = z.object({
  name: z.string(),
  cost: z.number(),
  sortOrder: z.number(),
});

export const MarketFacilityFormResponseRoomTypeDtoSchema = z.object({
  id: z.number(),
  type: RoomTypeEnum,
  baseRate: z.number(),
  sortOrder: z.number(),
});

export const MarketFacilityFormResponseExtraServiceDtoSchema = z.object({
  name: z.string(),
  price: z.number(),
  sortOrder: z.number(),
});

export const MarketFacilityFormResponseFacilityDtoSchema = z.object({
  id: z.number(),
  name: z.string(),
  dba: z.string(),
  yearBuilt: z.number().nullable(),
  address: z.string().nullable(),
  coordinates: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zipCode: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  website: z.string().nullable(),
});

export const AmenityConfigDtoSchema = z.object({
  facilityTypeId: z.number(),
  points: z.number(),
  priority: z.string(),
});

export const MarketFacilityFormResponseAmenityDtoSchema = z.object({
  id: z.number(),
  name: z.string(),
  isSelected: z.boolean(),
  configs: z.array(AmenityConfigDtoSchema),
});

export const MarketFacilityFormResponseAmenityCategoryDtoSchema = z.object({
  category: z.object({
    id: z.number(),
    name: z.string(),
  }),
  amenities: z.array(MarketFacilityFormResponseAmenityDtoSchema),
});

export const CalculatedAmenitiesPointsDtoSchema = z.object({
  generalTotalScore: z.number(),
  mcTotalScore: z.number(),
  general: z.array(
    z.object({
      priority: z.string(),
      points: z.number(),
    })
  ),
  mc: z.array(
    z.object({
      priority: z.string(),
      points: z.number(),
    })
  ),
});

export const MarketFacilityFormResponseDtoSchema = z.object({
  id: z.number(),
  facility: MarketFacilityFormResponseFacilityDtoSchema,
  types: z.array(FacilityTypeEnum),
  status: FormStatusEnum,
  version: z.number(),
  totalBeds: z.number().nullable(),
  secureBeds: z.number().nullable(),
  medicaid: z.boolean(),
  typicalStaffToResidentRatio: z.number().nullable(),
  skilledNursingOffered: z.boolean(),
  moveInFee: z.number().nullable(),
  moveInRefundable: z.boolean(),
  careStructure: CareStructureEnum.nullable(),
  levelsOfCare: z.array(MarketFacilityFormResponseCareLevelDtoSchema),
  roomTypes: z.array(MarketFacilityFormResponseRoomTypeDtoSchema),
  extraServices: z.array(MarketFacilityFormResponseExtraServiceDtoSchema),
  ownerName: z.string().nullable(),
  administrator: z.string().nullable(),
  marketingContact: z.string().nullable(),
  admissionsContact: z.string().nullable(),
  marketPosition: MarketPositionEnum.nullable(),
  awards: z.array(z.string()),
  sourceFacilityStaffCall: z.boolean(),
  sourceFacilityWebsite: z.boolean(),
  sourceStateDb: z.boolean(),
  sourceEmailWithFacility: z.boolean(),
  sourceOnSiteVisit: z.boolean(),
  sourceMarketingBrochure: z.boolean(),
  sourcePublicListing: z.boolean(),
  sourceGoogleSearch: z.boolean(),
  sourceOther: z.boolean(),
  collectedAt: z.string().nullable(),
  interestedCustomer: z.boolean(),
  notes: z.string().nullable(),
  updatedAt: z.string().nullable(),
  submittedAt: z.string().nullable(),
  submittedBy: UserDtoSchema.nullable(),
  collectedBy: UserDtoSchema.nullable(),
  amenities: z.array(MarketFacilityFormResponseAmenityCategoryDtoSchema),
  calculatedAmenitiesPoints: CalculatedAmenitiesPointsDtoSchema,
});

export const UpdateMarketFacilityFormRequestDtoSchema = z.object({
  coordinates: z
    .object({
      lat: z.number().nullable(),
      lng: z.number().nullable(),
    })
    .optional(),
  administrator: z.string().optional(),
  admissionsContact: z.string().optional(),
  amenities: z.array(z.number()).optional(),
  awards: z.array(z.string()).optional(),
  careStructure: CareStructureEnum.optional(),
  collectedAt: z.string().optional(),
  collectedBy: z.number().optional(),
  extraServices: z.array(MarketFacilityCreateExtraServiceDtoSchema).optional(),
  interestedCustomer: z.boolean().optional(),
  levelsOfCare: z.array(MarketFacilityCreateCareLevelDtoSchema).optional(),
  marketingContact: z.string().optional(),
  marketPosition: MarketPositionEnum.optional(),
  medicaid: z.boolean().optional(),
  moveInFee: z.number().optional(),
  moveInRefundable: z.boolean().optional(),
  notes: z.string().optional(),
  ownerName: z.string().optional(),
  phoneNumber: z.string().optional(),
  roomTypes: z.array(MarketFacilityCreateRoomTypeDtoSchema).optional(),
  secureBeds: z.number().optional(),
  skilledNursingOffered: z.boolean().optional(),
  sourceEmailWithFacility: z.boolean().optional(),
  sourceFacilityStaffCall: z.boolean().optional(),
  sourceFacilityWebsite: z.boolean().optional(),
  sourceGoogleSearch: z.boolean().optional(),
  sourceMarketingBrochure: z.boolean().optional(),
  sourceOnSiteVisit: z.boolean().optional(),
  sourceOther: z.boolean().optional(),
  sourcePublicListing: z.boolean().optional(),
  sourceStateDb: z.boolean().optional(),
  status: FormStatusEnum.optional(),
  totalBeds: z.number().optional(),
  types: z.array(FacilityTypeEnum).optional(),
  typicalStaffToResidentRatio: z.number().optional(),
  website: z.string().optional(),
  yearBuilt: z.number().optional(),
});

export const PayerTypeSchema = z.enum(['MEDICAID', 'PRIVATE']);

export const PayPositionCategorySchema = z.enum([
  'UNDERPAYING',
  'ON_TARGET',
  'OVERPAYING',
]);
export type PayPositionCategory = z.infer<typeof PayPositionCategorySchema>;

export const ResidentsDtoSchema = z.object({
  activeAlerts: z.number().nullable(),
  careLevelName: z.string().nullable(),
  createdAlerts: z.number().nullable(),
  daysOnCareLevel: z.number().nullable(),
  daysSinceReview: z.number().nullable(),
  facilityName: z.string(),
  flagged: z.boolean(),
  fullName: z.string(),
  id: z.number(),
  lengthOfStay: z.number().nullable(),
  payType: z.string().nullable(),
  rate: z.number().nullable(),
  revenue: z.number().nullable(),
  medianRate: z.number().nullable(),
  payPositionPct: z.number().nullable(),
  payPosition: PayPositionCategorySchema.nullable(),
});

export type UserDto = z.infer<typeof UserDtoSchema>;
export type RoleDto = z.infer<typeof UserRoleDtoSchema>;
export type OrganizationDto = z.infer<typeof OrganizationDtoSchema>;
export type OrganizationOwnerDto = z.infer<typeof OrganizationOwnerDtoSchema>;
export type EmrIntegrationDto = z.infer<typeof EmrIntegrationDtoSchema>;
export type FacilityDto = z.infer<typeof FacilityDtoSchema>;
export type FacilitySinglePageDto = z.infer<typeof FacilitySinglePageDtoSchema>;
export type AmenityDto = z.infer<typeof AmenityDtoSchema>;
export type StaffDto = z.infer<typeof StaffDtoSchema>;
export type SparkLinePointDto = z.infer<typeof SparkLinePointDtoSchema>;
export type AlertConfigDto = z.infer<typeof AlertConfigDtoSchema>;
export type MarketFacilityListItemDto = z.infer<
  typeof MarketFacilityListItemDtoSchema
>;
export type MarketFacilityVersionFormDto = z.infer<
  typeof MarketFacilityVersionFormDtoSchema
>;
export type MarketIntelDto = z.infer<typeof MarketIntelDtoSchema>;
export type AiSummaryDto = z.infer<typeof AiSummaryDtoSchema>;
export type CompetitorsLocationsDto = z.infer<
  typeof CompetitorsLocationsDtoSchema
>;
export type MarketFacilityFormResponseDto = z.infer<
  typeof MarketFacilityFormResponseDtoSchema
>;
export type MarketFacilityCreateCareLevelDto = z.infer<
  typeof MarketFacilityCreateCareLevelDtoSchema
>;
export type MarketFacilityCreateRoomTypeDto = z.infer<
  typeof MarketFacilityCreateRoomTypeDtoSchema
>;
export type MarketFacilityCreateExtraServiceDto = z.infer<
  typeof MarketFacilityCreateExtraServiceDtoSchema
>;
export type UpdateMarketFacilityFormRequestDto = z.infer<
  typeof UpdateMarketFacilityFormRequestDtoSchema
>;
export type MarketFacilityFormResponseFacilityDto = z.infer<
  typeof MarketFacilityFormResponseFacilityDtoSchema
>;
export type ResidentsDto = z.infer<typeof ResidentsDtoSchema>;
export type PayerType = z.infer<typeof PayerTypeSchema>;
